if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          // this.updateMedia(html, variant?.featured_media?.id); // commented because as we refreshing the media
          // Desktop we scroll the element
          if (window.innerWidth > 767 && variant?.featured_media?.id) {
            const wrapper = this.querySelector('.product-media-container');
            if (wrapper) {
              const targetMedia = wrapper.querySelector(
                `[data-media-id="${variant.featured_media.id}"]`
              );
              if (targetMedia) {
                wrapper.scrollTo({
                  top: targetMedia.offsetTop - 20,
                  behavior: "smooth"
                });
              }
            }
          }

          // Mobile Slider
          if (window.innerWidth <= 767 && variant?.featured_media?.id) {
            const slides = this.querySelectorAll('.media-item');
            slides.forEach((slide, index) => {
              if (parseInt(slide.dataset.mediaId) === variant.featured_media.id) {
                $('.mobile-main-slider').slick('slickGoTo', index);
              }
            });
          }

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };
          updateSourceFromDestination('MediaGallery');
          document.querySelectorAll('media-gallery').forEach((gallery) => {
            gallery.refresh();
          });
          updateSourceFromDestination('bulk_by');
          updateSourceFromDestination('polarbox');
          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('qtyUpdateCall');
          this.updateBackInStockVisibility();
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
      
          // Refresh the ATC sticky after the variant change.
          updateSourceFromDestination('stickyBar');

          // Refresh the slider on the variant change.
          //updateSourceFromDestination('productImageGallery');

          // Refresh the selling plan dropdown after the variant change.
          initSellingPlanDropdowns();

          if (typeof window.initProductGallery === 'function') {
            window.initProductGallery();
          }
          if (typeof window.bindVideoThumbs === 'function') {
            window.bindVideoThumbs();
          }

          // Mobile slider reinit
          if (window.innerWidth <= 767) {
            const $main = $('.mobile-main-slider');
            const $thumb = $('.mobile-thumb-slider');
            if ($main.hasClass('slick-initialized')) $main.slick('destroy');
            if ($thumb.hasClass('slick-initialized')) $thumb.slick('destroy');
            initMobileSlider();
          }

          if (window.HulkappWishlist && typeof window.HulkappWishlist.init === "function") {
            window.HulkappWishlist.init();
          }
          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          // input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateBackInStockVisibility() {
        const sectionEl = document.querySelector('[id^="qtyUpdateCall-"]');
        const formEl = document.querySelector('.backinstock-form');
        const bsubWidget = document.querySelector('.bsub-widget');

        if (!sectionEl || !formEl) return;

        const addToCartBtn = sectionEl.querySelector('.product_qty_add_to_cart_btn');
        if (!addToCartBtn) return;

        const isAvailable = addToCartBtn.classList.contains('available_variant');

        if (isAvailable) {
          formEl.classList.remove('soldout_variant');
          formEl.classList.add('available_variant');

          // Show bsub-widget
          if (bsubWidget) {
            bsubWidget.style.display = '';
          }

        } else {
          formEl.classList.remove('available_variant');
          formEl.classList.add('soldout_variant');

          // Hide bsub-widget
          if (bsubWidget) {
            bsubWidget.style.display = 'none';
          }
        }
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

// Mobile slider JS 
function initMobileSlider() {
  if (window.innerWidth > 767) return;

  const $main = $('.mobile-main-slider');
  const $thumb = $('.mobile-thumb-slider');

  if (!$main.length || !$thumb.length) return;
  if ($main.hasClass('slick-initialized')) return;
  if (!$.fn.slick) return;

  $main.slick({
    slidesToShow: 1,
    arrows: false,
    fade: true,
    asNavFor: '.mobile-thumb-slider'
  });

  $thumb.slick({
    slidesToShow: 6,
    slidesToScroll: 1,
    vertical: true,
    verticalSwiping: false,
    focusOnSelect: true,
    arrows: true,
    asNavFor: '.mobile-main-slider'
  });
}

document.addEventListener('DOMContentLoaded', initMobileSlider);
// For the product video 
(function () {
    const overlay = document.getElementById('video-popup-overlay');
    const contentBox = document.getElementById('video-popup-content');
    const closeBtn = overlay ? overlay.querySelector('.video-popup-close') : null;

    function openVideoPopup(thumbItem) {
      if (!overlay || !contentBox) return;

      const videoType = thumbItem.dataset.videoType;
      const videoSources = thumbItem.dataset.videoSources; 
      const videoId = thumbItem.dataset.videoId;

      contentBox.innerHTML = '';

      if (videoType === 'native') {
        const video = document.createElement('video');
        video.controls = true;
        video.autoplay = true;
        video.playsinline = true;
        video.preload = 'auto';

        (videoSources || '').split('||').forEach(function (pair) {
          const parts = pair.split('|');
          if (parts[0]) {
            const source = document.createElement('source');
            source.src  = parts[0];
            source.type = parts[1] || '';
            video.appendChild(source);
          }
        });

        contentBox.appendChild(video);

      } else if (videoType === 'youtube') {
        const iframe = document.createElement('iframe');
        iframe.src = 'https://www.youtube.com/embed/' + videoId + '?autoplay=1&rel=0&playsinline=1';
        iframe.allow = 'autoplay; fullscreen';
        iframe.allowFullscreen = true;
        contentBox.appendChild(iframe);

      } else if (videoType === 'vimeo') {
        const iframe = document.createElement('iframe');
        iframe.src = 'https://player.vimeo.com/video/' + videoId + '?autoplay=1&playsinline=1';
        iframe.allow = 'autoplay; fullscreen';
        iframe.allowFullscreen = true;
        contentBox.appendChild(iframe);
      }

      overlay.classList.add('is-open');
      overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    function closeVideoPopup() {
      if (!overlay || !contentBox) return;
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
      contentBox.innerHTML = '';
      document.body.style.overflow = '';
    }

    // Close button
    if (closeBtn) closeBtn.addEventListener('click', closeVideoPopup);

    // Click outside the inner box
    if (overlay) {
      overlay.addEventListener('click', function (e) {
        if (e.target === overlay) closeVideoPopup();
      });
    }

    // ESC key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeVideoPopup();
    });

    
    function bindVideoThumbs() {
      document.querySelectorAll('#product-gallery .video-thumb-item').forEach(function (item) {
        if (item.dataset.videoBound) return;
        item.dataset.videoBound = 'true';

        item.addEventListener('click', function () {
          openVideoPopup(item);
        });
      });
    }

    document.addEventListener('DOMContentLoaded', bindVideoThumbs);
    // Expose so variant-swap logic can rebind after DOM update
    window.bindVideoThumbs = bindVideoThumbs;
  })();

  function initSellingPlanDropdowns() {
    const selects = document.querySelectorAll('.bsub-selling-plan-update');

    if (!selects.length) return;

    selects.forEach(select => {
      // 👉 Destroy existing Choices instance if already initialized
      if (select.choicesInstance) {
        select.choicesInstance.destroy();
      }

      // 👉 Create new instance and store reference
      const instance = new Choices(select, {
        searchEnabled: false,
        itemSelectText: '',
        shouldSort: false
      });

      select.choicesInstance = instance;

      // 👉 Remove old listener (safe reset)
      select.removeEventListener('change', handleSellingPlanChange);
      select.addEventListener('change', handleSellingPlanChange);
    });
  }

  function initSellingPlanDropdowns() {
    const selects = document.querySelectorAll('.bsub-selling-plan-update');
    selects.forEach(select => {
      if (select.choicesInstance) return;

      const instance = new Choices(select, {
        searchEnabled: false,
        itemSelectText: '',
        shouldSort: false
      });

      select.choicesInstance = instance;
      select.addEventListener('change', handleSellingPlanChange);
    });
  }

  document.addEventListener('change', function (e) {
    if (e.target.matches('[data-bsub-selling-plan-group-input]')) {
      setTimeout(() => {
        syncSellingPlanDropdowns();
      }, 100);
    }
  });

  function syncSellingPlanDropdowns() {
    document.querySelectorAll('.bsub-selling-plan-update').forEach(select => {

      const instance = select.choicesInstance;
      if (!instance) return;

      const form = select.closest("form");

      const selectedGroup = form?.querySelector(
        '[data-bsub-selling-plan-group-input]:checked'
      );

      const isOneTime = selectedGroup?.value === 'once';

      const hiddenInput = form?.querySelector(
        "[data-bsub-selling-plan-id-input]"
      );

      if (isOneTime) {
        if (hiddenInput) hiddenInput.value = '';
        return;
      }

      let value = select.value;

      if (!value) {
        const firstOption = select.querySelector('option[value]');
        if (firstOption) {
          value = firstOption.value;
          select.value = value;
        }
      }

      instance.setChoiceByValue(value);

      if (hiddenInput) {
        hiddenInput.value = value;
      }
    });
  }

  function handleSellingPlanChange(e) {
    const selectedValue = e.target.value;

    const hiddenInput = e.target.closest("form")?.querySelector(
      "[data-bsub-selling-plan-id-input]"
    );

    if (hiddenInput) {
      hiddenInput.value = selectedValue;
    }
  }

  //  For the Product Zoom Feature
  function initProductGallery() {
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      const zoomScaleFactor = 2.5;

      document.querySelectorAll('#product-gallery .zoom-item').forEach(function (galleryItem) {
        const highResolutionImageUrl = galleryItem.getAttribute('data-image');
        let isHighResolutionImagePreloaded = false;

        const updateZoomPosition = function (mouseEvent) {
          const elementBounds = galleryItem.getBoundingClientRect();

          const horizontalCursorRatio = Math.min(
            Math.max((mouseEvent.clientX - elementBounds.left) / elementBounds.width, 0), 1
          );
          const verticalCursorRatio = Math.min(
            Math.max((mouseEvent.clientY - elementBounds.top) / elementBounds.height, 0), 1
          );

          const zoomedBackgroundWidth  = elementBounds.width  * zoomScaleFactor;
          const zoomedBackgroundHeight = elementBounds.height * zoomScaleFactor;

          galleryItem.style.backgroundSize     = zoomedBackgroundWidth + 'px ' + zoomedBackgroundHeight + 'px';
          galleryItem.style.backgroundPosition =
            (horizontalCursorRatio * elementBounds.width - horizontalCursorRatio * zoomedBackgroundWidth) + 'px ' +
            (verticalCursorRatio * elementBounds.height - verticalCursorRatio * zoomedBackgroundHeight) + 'px';
        };

        galleryItem.addEventListener('mouseenter', function (mouseEvent) {
          if (!isHighResolutionImagePreloaded) {
            new Image().src = highResolutionImageUrl;
            isHighResolutionImagePreloaded = true;
          }
          galleryItem.style.backgroundImage  = "url('" + highResolutionImageUrl + "')";
          galleryItem.style.backgroundRepeat = 'no-repeat';
          galleryItem.classList.add('zoomed');
          updateZoomPosition(mouseEvent);
        });

        galleryItem.addEventListener('mousemove', updateZoomPosition);

        galleryItem.addEventListener('mouseleave', function () {
          galleryItem.classList.remove('zoomed');
          galleryItem.style.backgroundImage  = '';
          galleryItem.style.backgroundSize   = '';
          galleryItem.style.backgroundPosition = '';
        });
      });
    }
  }

  document.addEventListener('DOMContentLoaded', initProductGallery);
  document.addEventListener('DOMContentLoaded', initSellingPlanDropdowns);
  window.initProductGallery = initProductGallery;